import moment from "moment";

/** Fairfield City Council booking times are always interpreted in NSW local time. */
export const BOOKING_LOCALE = "en-AU";
export const BOOKING_TIME_ZONE = "Australia/Sydney";

const PARSE_FORMATS = [
  "DD/MM/YYYY HH:mm",
  "D/M/YYYY H:mm",
  "DD/M/YYYY HH:mm",
  "D/MM/YYYY H:mm",
];

export function getBookingWeekdayName(date) {
  if (!date) return "";
  return date.toLocaleDateString(BOOKING_LOCALE, { weekday: "long" });
}

function extractBookingTimePart(value) {
  if (value == null) {
    return "";
  }

  const text = String(value).trim();
  if (!text) {
    return "";
  }

  const datetimeMatch = text.match(/\s(\d{1,2}:\d{2})(?::\d{2})?$/);
  if (datetimeMatch) {
    return datetimeMatch[1];
  }

  if (/^\d{1,2}:\d{2}$/.test(text)) {
    return text;
  }

  return "";
}

function toTwelveHourClock(hours, minutes) {
  const period = hours >= 12 ? "PM" : "AM";
  let displayHours = hours % 12;
  if (displayHours === 0) {
    displayHours = 12;
  }
  return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
}

/** Display API / booking times as 12-hour clock (e.g. 01:00 PM). */
export function formatBookingTimeLabel(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toTwelveHourClock(value.getHours(), value.getMinutes());
  }

  const timePart = extractBookingTimePart(value);
  if (!timePart) {
    return value ? String(value).trim() : "";
  }

  const [hoursPart, minutesPart = "00"] = timePart.split(":");
  const hours = parseInt(hoursPart, 10);
  const minutes = parseInt(minutesPart, 10) || 0;
  if (Number.isNaN(hours)) {
    return timePart;
  }

  return toTwelveHourClock(hours, minutes);
}

export function formatBookingTimeRange(startValue, endValue) {
  const start = formatBookingTimeLabel(startValue);
  const end = formatBookingTimeLabel(endValue);
  if (!start || !end) {
    return "—";
  }
  return `${start} – ${end}`;
}

export function normalizeBookingDateTimeString(dateStr) {
  if (!dateStr || typeof dateStr !== "string") {
    return null;
  }

  let normalized = dateStr.trim();
  normalized = normalized.replace(/(\d{2}\/\d{2}\/\d{4})(\d{1,2}:\d{2})/, "$1 $2");

  const has24Hour = normalized.includes("24:00");
  if (has24Hour) {
    normalized = normalized.replace(/24:00/g, "00:00");
  }

  normalized = normalized.replace(/\s+0:(\d{2})/g, " 00:$1");
  normalized = normalized.replace(/^0:(\d{2})/, "00:$1");
  normalized = normalized.replace(/\s+(\d):(\d{2})/g, (_match, hour, min) => {
    return ` ${hour.padStart(2, "0")}:${min}`;
  });

  const parts = normalized.split(/\s+/);
  if (parts.length >= 2) {
    const datePart = parts[0];
    let timePart = parts[1];
    const timeMatch = timePart.match(/(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      timePart = `${timeMatch[1].padStart(2, "0")}:${timeMatch[2]}`;
    }
    normalized = `${datePart} ${timePart}`;
  }

  return {
    normalized,
    needsDayAdjustment: has24Hour,
  };
}

function parseMomentFromNormalized(normalized) {
  if (!normalized?.normalized) {
    return null;
  }

  let parsed = moment(normalized.normalized, "DD/MM/YYYY HH:mm", true);
  if (!parsed.isValid()) {
    parsed = moment(normalized.normalized, PARSE_FORMATS, true);
  }
  if (!parsed.isValid()) {
    return null;
  }

  if (normalized.needsDayAdjustment) {
    parsed = parsed.clone().add(1, "day");
  }

  return parsed;
}

/** Parse API / form datetime (DD/MM/YYYY HH:mm) as local wall-clock time. */
export function parseBookingDateTime(dateStr) {
  const normalized = normalizeBookingDateTimeString(dateStr);
  const parsed = parseMomentFromNormalized(normalized);
  if (!parsed) {
    return null;
  }

  return new Date(
    parsed.year(),
    parsed.month(),
    parsed.date(),
    parsed.hours(),
    parsed.minutes(),
    0,
    0
  );
}

export function parseBookingDateTimeRange(startStr, endStr) {
  const startNormalized = normalizeBookingDateTimeString(startStr);
  const endNormalized = normalizeBookingDateTimeString(endStr);
  if (!startNormalized || !endNormalized) {
    return null;
  }

  const startMoment = parseMomentFromNormalized(startNormalized);
  const endMoment = parseMomentFromNormalized(endNormalized);
  if (!startMoment || !endMoment) {
    return null;
  }

  const start = new Date(
    startMoment.year(),
    startMoment.month(),
    startMoment.date(),
    startMoment.hours(),
    startMoment.minutes(),
    0,
    0
  );
  const end = new Date(
    endMoment.year(),
    endMoment.month(),
    endMoment.date(),
    endMoment.hours(),
    endMoment.minutes(),
    0,
    0
  );

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  return { start, end };
}

export function formatLocalDateKey(date) {
  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatCalendarLocalDateTime(date) {
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export function parseCalendarLocalDateTime(value) {
  if (!value || typeof value !== "string") {
    return null;
  }

  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (!match) {
    return null;
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6] || 0),
    0
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toCalendarEventInput({
  start,
  end,
  title = "Booked",
  classNames = ["booking-cal-event"],
  extendedProps = {},
}) {
  return {
    title,
    start: formatCalendarLocalDateTime(start),
    end: formatCalendarLocalDateTime(end),
    classNames,
    backgroundColor: "transparent",
    borderColor: "transparent",
    textColor: "inherit",
    extendedProps,
  };
}
